import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronUp, Download, BarChart2, List, ChevronLeft, ChevronRight } from "lucide-react";
import CostExplorerExport, { exportCostExplorerDataToCsv } from '../CostExplorerExport';
const CostExplorer = ({ 
  data, 
  filters, 
  handleFilterChange,
  loading = false,
  isUpdating = false
}) => {
  useEffect(() => {console.log("data is changed") }, [handleFilterChange,data,filters]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [chartType, setChartType] = useState('stacked');
  const [viewMode, setViewMode] = useState('graph');
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const PRIMARY_COLOR = '#003366';

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };
 

  const DIMENSION_OPTIONS = ["account", "service", "region","apiOperation"].map(dim => ({ value: dim }));
  const GRANULARITY_OPTIONS = ["daily", "monthly"].map(option => ({ value: option }));

  // Handle single select filter change
  const handleSingleSelect = (name, value) => {
    handleFilterChange({
      target: {
        name: name,
        value: value
      }
    });
    setOpenDropdown(null);
  };
  useEffect(() => {
  const handleClickOutside = (event) => {
    // Check if the click is outside any dropdown
    if (!event.target.closest('.dropdown-container')) {
      setOpenDropdown(null);
    }
  };

  if (openDropdown) {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }
}, [openDropdown]);

  // Render compact filter dropdown
 const renderCompactFilter = (name, options) => {
  const selectedValue = filters[name] || "";
  const isOpen = openDropdown === name;
  
  const displayValue = selectedValue !== "" ? 
    options.find(opt => opt.value === selectedValue)?.value || selectedValue : 
    options[0].value;
  
  return (
    <div className="relative inline-block dropdown-container"> {/* Add dropdown-container class */}
      <button 
        className="flex items-center text-sm px-3 py-1.5 border border-gray-200 rounded-md bg-white hover:border-gray-400 transition-colors"
        style={{ 
          borderColor: isOpen ? '#003366' : undefined,
          '--tw-ring-color': '#003366'
        }}
        onClick={() => toggleDropdown(name)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Select ${name}`}
      >
        <span className="mr-2">{displayValue}</span>
        {isOpen ? 
          <ChevronUp className="h-3.5 w-3.5 text-gray-500" /> : 
          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
        }
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-20 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto" 
          style={{width: 'max-content', minWidth: '100%'}}
          role="menu"
        >
          {options.map((option) => (
            <div 
              key={option.value} 
              className={`px-3 py-1.5 hover:bg-gray-50 text-sm cursor-pointer ${selectedValue === option.value ? 'font-medium text-gray-900' : ''}`}
              style={{
                backgroundColor: selectedValue === option.value ? '#e6f0ff' : undefined,
                color: selectedValue === option.value ? '#003366' : undefined
              }}
              onClick={() => handleSingleSelect(name, option.value)}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSingleSelect(name, option.value);
                }
              }}
            >
              {option.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
  // Format currency for tooltips and table
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Format X-Axis labels based on granularity
  const formatXAxisLabel = (dateStr) => {
    try {
      if (!dateStr) return '';
      
      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      
      if (filters.granularity === 'daily') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    } catch (error) {
      return dateStr;
    }
  };

  // Memoized chart data processing
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const processedData = [];
    
    // For each date entry
    data.forEach(item => {
      const dateItem = { date: item.date };
      const serviceValues = [];
      
      // Extract all service costs for this date
      Object.keys(item).forEach(key => {
        if (key !== 'date') {
          serviceValues.push({ key, value: item[key] || 0 });
        }
      });
      
      // Sort services by cost in descending order
      serviceValues.sort((a, b) => b.value - a.value);
      
      // Add top 7 services to the date item
      serviceValues.slice(0, 7).forEach(service => {
        dateItem[service.key] = service.value;
      });
      
      // Merge costs of all other services into "Others"
      if (serviceValues.length > 7) {
        const othersValue = serviceValues.slice(7).reduce((sum, service) => sum + service.value, 0);
        dateItem['Others'] = othersValue;
      }
      
      processedData.push(dateItem);
    });
    console.log("Processed Data:", processedData);
    return processedData;
  }, [data]);

  const tableData = useMemo(() => {
  console.log("Original Data:", data);
  if (!data || data.length === 0) return { headers: [], rows: [], totals: {} };
  
  // Extract all unique service/account/region keys
  const allKeys = new Set();
  data.forEach(entry => {
    Object.keys(entry).forEach(key => {
      if (key !== 'date') allKeys.add(key);
    });
  });
  
  // Create column headers - first column will be dimension label based on selected groupBy
  const dimensionLabel = filters.groupBy ? filters.groupBy.charAt(0).toUpperCase() + filters.groupBy.slice(1) : "Service";
  const totalLabel = `${dimensionLabel} Total`;
  const headers = [dimensionLabel, totalLabel, ...data.map(entry => formatXAxisLabel(entry.date))];
  
  // Calculate total for each dimension (service/account/region)
  const dimensionTotals = {};
  Array.from(allKeys).forEach(key => {
    let total = 0;
    data.forEach(entry => {
      total += entry[key] || 0;
    });
    dimensionTotals[key] = total;
  });
  
  // Create transposed rows - one row per dimension with values for each date
  const rows = Array.from(allKeys).sort().map(key => {
    const rowData = { [dimensionLabel]: key };
    let rowTotal = 0;
    
    // Add values for each date
    data.forEach(entry => {
      const dateKey = formatXAxisLabel(entry.date);
      const value = entry[key] || 0;
      rowData[dateKey] = formatCurrency(value);
      rowTotal += value; // Add to row total before formatting
    });
    
    // Add formatted total as second column
    rowData[totalLabel] = formatCurrency(rowTotal);
    return rowData;
  });
  
  // Calculate date totals
  const dateTotals = {};
  data.forEach(entry => {
    const dateKey = formatXAxisLabel(entry.date);
    let dateTotal = 0;
    
    Object.keys(entry).forEach(key => {
      if (key !== 'date') {
        dateTotal += entry[key] || 0;
      }
    });
    
    dateTotals[dateKey] = dateTotal;
  });
  
  // Create "Total" row and place it at the beginning
  const totalRow = { [dimensionLabel]: 'Total Costs' };
  let grandTotal = 0;
  
  data.forEach(entry => {
    const dateKey = formatXAxisLabel(entry.date);
    const dateTotal = dateTotals[dateKey] || 0;
    totalRow[dateKey] = formatCurrency(dateTotal);
    grandTotal += dateTotal;
  });
  
  totalRow[totalLabel] = formatCurrency(grandTotal);
  rows.unshift(totalRow); // Add total row at the beginning

  
  return { headers, rows, dimensionTotals, dateTotals };
}, [data, formatXAxisLabel, filters.groupBy]);

  const totalPages = useMemo(() => Math.ceil(tableData.rows.length / rowsPerPage), [tableData.rows.length, rowsPerPage]);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const displayedRows = tableData.rows.slice(startIndex, startIndex + rowsPerPage);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Memoized dynamic bars generation with Cloudscape colors
  const dynamicBars = useMemo(() => {
    if (!processedData || processedData.length === 0) return null;
    const uniqueKeys = new Set();
    processedData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'date') uniqueKeys.add(key);
      });
    });
    const colors = {
      EC2: '#FF9900',
      S3: '#1E88E5',
      DynamoDB: '#7E57C2',
      Lambda: '#26A69A',
      CloudWatch: '#43A047',
      RDS: '#5E35B1',
      ECS: '#FB8C00',
      SQS: '#F4511E',
      Others: '#78909C',
      default: [
        '#FF9900', '#1E88E5', '#7E57C2', '#26A69A', '#43A047', 
        '#5E35B1', '#FB8C00', '#F4511E', '#6D4C41', '#546E7A'
      ]
    };
    let colorIndex = 0;
    return Array.from(uniqueKeys).map(key => {
      let fill;
      if (key === 'Others') {
        fill = colors.Others;
      } else if (colors[key]) {
        fill = colors[key];
      } else {
        fill = colors.default[colorIndex % colors.default.length];
        colorIndex++;
      }

      if (chartType === 'stacked') {
        return <Bar key={key} dataKey={key} stackId="a" fill={fill} />;
      } else {
        return <Bar key={key} dataKey={key} fill={fill} />;
      }
    });
  }, [processedData, chartType]);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Helper function to truncate text to 30 characters
      const truncateName = (text) => {
        if (text.length <= 30) return text;
        return text.substring(0, 27) + '... ';
      };
      
      return (
        <div className="bg-white p-4 border border-gray-200 shadow-lg rounded-md">
          <p className="font-semibold text-gray-700">{formatXAxisLabel(label)}</p>
          <div className="mt-2 space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <p className="text-sm">
                  <span className="font-medium">{truncateName(entry.name)}:</span>{' '}
                  {formatCurrency(entry.value)}
                </p>
              </div>
            ))}
            <div className="pt-1 mt-1 border-t border-gray-200">
              <p className="text-sm font-semibold">
                Total:{' '}
                {formatCurrency(
                  payload.reduce((sum, entry) => sum + entry.value, 0)
                )}
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend that only shows key items in a smaller font
  const CustomLegend = (props) => {
    const { payload } = props;
    
    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2 text-xs">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center">
            <div 
              className="w-2 h-2 rounded-full mr-1" 
              style={{ backgroundColor: entry.color }} 
            />
            <span className="text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

 // Updated CostExplorerTable component to display transposed table format
const CostExplorerTable = ({ tableData, displayedRows, currentPage, totalPages, goToPage }) => {
  console.log("Table Data:", tableData);

  // Function to truncate dimension names
  const truncateName = (name, maxLength = 15) => {
    if (!name) return '';
    if (name === 'Total') return name;
    
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  const dimensionLabel = tableData.headers[0];
  const totalLabel = tableData.headers[1];
  
  return (
    <div className="w-full h-full flex flex-col">
      {/* Scrollable table container */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" aria-label="Cost data table">
          <thead className="bg-gray-50">
            <tr>
              {tableData.headers.map((header, index) => (
                <th 
                  key={index} 
                  className={`p-2 text-left text-xs font-medium border-b border-gray-200 ${
                    index === 0 ? 'sticky left-0 bg-gray-50 z-10' : 
                    index === 1 ? 'font-semibold text-right' : 'text-right'
                  }`}
                  style={{ 
                    color: index === 1 ? '#003366' : '#6b7280',
                    minWidth: index === 0 ? '120px' : '80px'
                  }}
                  scope="col"
                  title={header}
                >
                  {index === 0 ? header : truncateName(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedRows.map((row, rowIndex) => (
              <tr 
                key={rowIndex} 
                className={`hover:bg-gray-50 ${row[dimensionLabel] === 'Total' ? 'font-semibold bg-gray-50' : ''}`}
              >
                {tableData.headers.map((header, colIndex) => (
                  <td 
                    key={`${rowIndex}-${colIndex}`} 
                    className={`p-2 ${
                      colIndex === 0 ? 'sticky left-0 z-10 hover:bg-gray-50 font-medium' :
                      colIndex === 1 ? 'font-semibold text-right' : 'text-right'
                    } ${row[dimensionLabel] === 'Total' ? (colIndex === 0 ? 'bg-gray-100' : 'bg-gray-50') : 
                      (colIndex === 0 ? 'bg-white' : '')}`}
                    style={{ 
                      color: (colIndex === 1 || row[dimensionLabel] === 'Total') ? '#003366' : ''
                    }}
                    title={`${header}: ${row[header]}`} // Show full info on hover
                  >
                    {colIndex === 0 ? truncateName(row[header], 20) : row[header]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination controls - aligned to right */}
      <div className="mt-4 flex items-center border-t border-gray-200 pt-3">
        <div className="flex-1 flex items-center">
          <div>
            <p className="text-sm text-gray-700">
              Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, tableData.rows.length)} of {tableData.rows.length} entries
            </p>
          </div>
          
          <div className="ml-auto flex items-center space-x-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded border ${
                currentPage === 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            {/* Pagination numbers */}
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`w-8 h-8 flex items-center justify-center rounded transition-colors`}
                  style={{
                    backgroundColor: currentPage === pageNum ? '#e6f0ff' : 'transparent',
                    color: currentPage === pageNum ? '#003366' : '#374151'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== pageNum) {
                      e.target.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== pageNum) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  aria-label={`Go to page ${pageNum}`}
                  aria-current={currentPage === pageNum ? "page" : undefined}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded border ${
                currentPage === totalPages
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with title and controls */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h1 className="text-xl font-semibold" style={{ color: '#003366' }}>Cost Explorer</h1>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex border border-gray-200 rounded-md">
            <button 
              className={`flex items-center px-3 py-1.5 text-sm transition-colors ${viewMode === 'graph' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              style={{ backgroundColor: viewMode === 'graph' ? '#003366' : '' }}
              onClick={() => setViewMode('graph')}
              aria-pressed={viewMode === 'graph'}
              aria-label="View as graph"
            >
              <BarChart2 className="h-4 w-4 mr-1" />
              Graph
            </button>
            <button 
              className={`flex items-center px-3 py-1.5 text-sm border-l border-gray-200 transition-colors ${viewMode === 'table' ? 'text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              style={{ backgroundColor: viewMode === 'table' ? '#003366' : '' }}
              onClick={() => setViewMode('table')}
              aria-pressed={viewMode === 'table'}
              aria-label="View as table"
            >
              <List className="h-4 w-4 mr-1" />
              Table
            </button>
          </div>
          
          {/* Export Button */}
          <CostExplorerExport tableData={tableData} />
        </div>
      </div>
      
      <div className="p-4">
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        )}
      
        {/* Chart Controls */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            {viewMode === 'graph' && (
              <>
                <button 
                  onClick={() => setChartType('stacked')} 
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${chartType === 'stacked' ? 'text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  style={{ backgroundColor: chartType === 'stacked' ? '#003366' : '' }}
                  aria-pressed={chartType === 'stacked'}
                  aria-label="View as stacked chart"
                >
                  Stacked
                </button>
                <button 
                  onClick={() => setChartType('grouped')} 
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${chartType === 'grouped' ? 'text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  style={{ backgroundColor: chartType === 'grouped' ? '#003366' : '' }}
                  aria-pressed={chartType === 'grouped'}
                  aria-label="View as grouped chart"
                >
                  Grouped
                </button>
              </>
            )}
          </div>
          
          {/* Compact Filter Controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2" id="groupby-label">Group By:</span>
              {renderCompactFilter("groupBy", DIMENSION_OPTIONS)}
            </div>
            
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2" id="granularity-label">Granularity:</span>
              {renderCompactFilter("granularity", GRANULARITY_OPTIONS)}
            </div>
          </div>
        </div>
        
        {/* Graph/Table View */}
        <div className="h-96 w-full relative">
          {(loading || isUpdating) && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] z-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
                <span className="text-sm text-gray-600">Updating data...</span>
              </div>
            </div>
          )}
          {(!data || data.length === 0 && !loading && !isUpdating) ? (
            <div className="h-80 flex items-center justify-center rounded-lg ">
                      <div className="text-center">
                        <p className="text-gray-500">No data available for the selected filters</p>
                        <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
                      </div>
                    </div>
          ) : viewMode === 'graph' ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={processedData}
                margin={{ top: 10, right: 30, left: 10, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxisLabel}
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value).toLocaleString()}`}
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  content={<CustomLegend />}
                  wrapperStyle={{ paddingTop: 10 }}
                  iconType="circle"
                  iconSize={8}
                />
                {dynamicBars}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <CostExplorerTable 
              tableData={tableData} 
              displayedRows={displayedRows}
              currentPage={currentPage}
              totalPages={totalPages}
              goToPage={goToPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CostExplorer;